import { Loader } from '@/helpers/Loader'
import { apiService } from '@/services/Api'
import { MktuClass, MktuClassWord } from '@/services/Api/generated'
import { exportMktuListToFile } from '@/shared/Mktu/utils'
import { Loader as SimpleLoader } from '@/store/Loader.store'
import {
  mktuClassListStore,
  MktuClassListStore,
} from '@/store/MktuClassList.store'
import { disableBodyScroll, enableBodyScroll } from '@/utils/DOM'
import { ObjectUtils } from '@/utils/object'
import { makeAutoObservable } from 'mobx'
import { MktuClassStore } from './Mktu/MktuClass.store'
import { SelectWordsStore } from './Mktu/SelectWords.store'

class PopupStore {
  isVisible = false

  show() {
    this.isVisible = true
    disableBodyScroll()
  }

  hide() {
    this.isVisible = false
    enableBodyScroll()
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }
}

export class MktuSearchStore {
  popupListSelectedWords = new PopupStore()
  popupMktuCLassList = new PopupStore()
  popupClassWords = new PopupStore()

  selectWordsStore = new SelectWordsStore()
  words: string[] = []

  classes: MktuClassStore[] = []
  foundedClasses: MktuClassStore[] = []

  classesLoader = new SimpleLoader()

  get mktuWords(): MktuClassWord[] {
    if (this.classes.length === 0 && this.words.length !== 0) {
      return this.words.map((word) => ({
        uuid: word,
        word,
        classNumber: 0,
        isNice: false,
      }))
    }

    return this.classes.reduce((acc, item) => {
      return [...acc, ...item.selectedWords]
    }, [] as MktuClassWord[])
  }

  get mapClassByNumber() {
    return this.classes.reduce((acc, item) => {
      acc.set(item.data.classNumber, item.words)

      return acc
    }, new Map())
  }

  get selectedClasses() {
    return this.classes
      .filter((c) => c.selectedWords.length !== 0)
      .sort((a, b) => a.data.classNumber - b.data.classNumber)
  }

  get classNumbers() {
    return this.selectedClasses.map((c) => c.data.classNumber)
  }

  exportFile = new Loader(async () => {
    await exportMktuListToFile(this.classes)
  })

  importFile = new Loader(async (file: File) => {
    const response = await apiService.mktu._import({
      file,
    })

    if (response.status) {
      const promises = response.data
        .map((mktuClassData) => {
          if (mktuClassData.classNumber != null) {
            const mktuClass = this.getClass(mktuClassData.classNumber)

            if (mktuClass != null && mktuClassData.selectedWords != null) {
              return mktuClass.setSelectedWordsNames(
                mktuClassData.selectedWords
              )
            }
          }
        })
        .filter((item) => item != null)

      await Promise.all(promises)
    }
  })

  constructor(public mktuClassListStore: MktuClassListStore) {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  async getClasses(classWords: MktuClassWord[]) {
    const words = classWords.map((w) => w.word)
    if (words.length === 0) {
      return []
    }
    this.classesLoader.startLoading()
    try {
      const response = await apiService.mktu.search({
        searchRequest: {
          words,
        },
      })

      if (response.status && response.data != null) {
        return ObjectUtils.entries(response.data.groups).map(
          ([classNumber, words]) => {
            return { classNumber: Number(classNumber), words }
          }
        )
      }
    } finally {
      this.classesLoader.stopLoading()
    }
  }

  async findClasses() {
    const classess = await this.getClasses(this.selectWordsStore.selectedWords)
    if (classess != null) {
      const mktuClasses = classess.map((item) => {
        const mktuClass = this.mktuClassListStore.getMktuClassByNumber(
          item.classNumber
        )
        const mktuClassStore = new MktuClassStore(mktuClass)
        mktuClassStore.setSelectedWords(item.words)

        return mktuClassStore
      })
      this.setFoundedClasses(mktuClasses)
    }
  }

  getClass(
    classNumber: MktuClass['classNumber'],
    initClassData?: MktuClass,
    initWords?: MktuClassWord[]
  ) {
    let mktuClass: MktuClassStore | null = null

    // Находимся в режиме поиска классов
    if (this.foundedClasses.length != 0) {
      const foundedClass = this.foundedClasses.find(
        (mktuClass) => mktuClass.data.classNumber === classNumber
      )
      if (foundedClass != null) {
        mktuClass = foundedClass
      }
    } else {
      // Ищем среди выбранных классов
      const selectedMktuClass = this.classes.find(
        (mktuClass) => mktuClass.data.classNumber === classNumber
      )
      if (selectedMktuClass != null) {
        mktuClass = selectedMktuClass
      }
    }

    // Если не найден, создаем новый класс
    if (mktuClass == null) {
      try {
        const classData =
          initClassData ??
          this.mktuClassListStore.getMktuClassByNumber(classNumber)

        mktuClass = new MktuClassStore(classData, initWords)
        this.classes = [...this.classes, mktuClass]
      } catch {}
    }

    // Передаем список слов
    if (
      mktuClass != null &&
      mktuClass.words.length === 0 &&
      initWords != null
    ) {
      mktuClass.setWords(initWords)
    }

    return mktuClass
  }

  setClasses(classes: MktuClassStore[]) {
    this.classes = classes
  }

  removeClass(classNumber: MktuClass['classNumber']) {
    this.classes = this.classes.filter(
      (mktuClass) => mktuClass.data.classNumber !== classNumber
    )
  }

  resetClasses() {
    this.classes = []
  }

  setFoundedClasses(classes: MktuClassStore[]) {
    this.foundedClasses = classes
  }

  resetSearch() {
    this.selectWordsStore.resetToDefault()
    this.foundedClasses = []
  }
}

export const mktuSearchStore = new MktuSearchStore(mktuClassListStore)
